import { useEffect, useState } from 'react';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { useToast } from '@/components/ui/use-toast';
import { AlertTriangle } from 'lucide-react';
import StaffAPI from '@/api/staff.api';
import { USER_PERMISSIONS, COLLABORATOR_PERMISSIONS_GROUP } from 'shared/constants/auth.constants';
import AltOwnerPicker from './alt-owner-picker';

const PERMISSION_OPTIONS = Object.values(USER_PERMISSIONS);

const isCollaboratorPlus = permissions => COLLABORATOR_PERMISSIONS_GROUP.includes(permissions);

export default function UpdateSubscriptionDialog({ userId, subscription, onClose, onSuccess }) {
  // Initialize from the subscription (no flash of an unrelated default).
  const [permissions, setPermissions] = useState(subscription?.permissions ?? '');
  const [altOwnerId, setAltOwnerId] = useState('');
  const [fetchingOwners, setFetchingOwners] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const { toast } = useToast();

  const open = !!subscription;
  const currentPermissions = subscription?.permissions;
  // Guard with `!!permissions` so the brief first render with `permissions=''`
  // (before the useEffect syncs from props) doesn't falsely flip to true and
  // mount AltOwnerPicker, which would race-leak fetchingOwners=true.
  const isOwnershipDowngrade =
    !!permissions && isCollaboratorPlus(currentPermissions) && !isCollaboratorPlus(permissions);

  useEffect(() => {
    if (subscription) setPermissions(subscription.permissions);
    setAltOwnerId('');
  }, [subscription]);

  const canSubmit =
    !!subscription &&
    !!permissions &&
    permissions !== currentPermissions &&
    (!isOwnershipDowngrade || !!altOwnerId);

  const onSubmit = async e => {
    e.preventDefault();
    if (!subscription) return;

    setSubmitting(true);
    try {
      const res = await StaffAPI.updateSubscription({
        userId,
        subscriptionId: subscription.id,
        permissions,
        altOwnerId: isOwnershipDowngrade ? altOwnerId : undefined,
      });

      if (res.error) {
        toast({
          title: 'Failed to update subscription',
          description: res.data?.message || res.error.message || 'Unexpected error',
          variant: 'destructive',
        });
        return;
      }

      toast({ title: `Updated permissions to ${permissions}` });
      onSuccess?.();
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={o => !o && onClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Edit subscription</DialogTitle>
          <DialogDescription>
            {subscription?.organizationName || 'Unknown organization'}
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={onSubmit} className="space-y-4">
          <div className="rounded-md border p-3 bg-muted/40 text-sm">
            <div className="text-xs text-muted-foreground">Current</div>
            <Badge variant="outline" className="mt-1">
              {currentPermissions}
            </Badge>
          </div>

          <div className="space-y-2">
            <Label htmlFor="permissions">New permission level</Label>
            <Select value={permissions} onValueChange={setPermissions} disabled={submitting}>
              <SelectTrigger id="permissions">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {PERMISSION_OPTIONS.map(p => (
                  <SelectItem key={p} value={p}>
                    {p}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {isOwnershipDowngrade && (
            <div className="space-y-3">
              <div className="rounded-md border border-destructive/40 p-3 bg-destructive/5">
                <div className="flex items-start gap-2">
                  <AlertTriangle className="h-4 w-4 mt-0.5 flex-shrink-0 text-destructive" />
                  <div className="space-y-1">
                    <div className="text-sm font-medium">
                      Dropping from {currentPermissions} to {permissions} removes ownership.
                    </div>
                    <div className="text-xs text-muted-foreground">
                      Knowledge, labs, admissions, rotations, and workspaces this user owns in{' '}
                      {subscription?.organizationName || 'this org'} will be transferred to the
                      fallback owner before the perms drop.
                    </div>
                  </div>
                </div>
              </div>

              <AltOwnerPicker
                active={isOwnershipDowngrade}
                userId={userId}
                subscriptionId={subscription?.id}
                value={altOwnerId}
                onChange={setAltOwnerId}
                disabled={submitting}
                onLoadingChange={setFetchingOwners}
                labelId="update-alt-owner"
              />
            </div>
          )}

          <DialogFooter>
            <Button type="button" variant="outline" onClick={onClose} disabled={submitting}>
              Cancel
            </Button>
            <Button type="submit" disabled={submitting || fetchingOwners || !canSubmit}>
              {submitting ? 'Saving...' : 'Save'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
